import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import React, { useState } from "react";
import { Document, Page } from 'react-pdf';

interface DocumentPreviewProps {
    open: boolean;
    onClose: () => void;
    url: string;
    mimeType: string;
}

const DocumentPreview: React.FC<DocumentPreviewProps> = ({ open, onClose, url, mimeType }) => {
    const [numPages, setNumPages] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
        // Don't reset currentPage if already in range
        setCurrentPage((prev) => prev > numPages ? numPages : prev < 1 ? 1 : prev);
    };

    return (
        <Dialog
            open={open}
            onClose={() => { }}
            fullWidth
            maxWidth="md"
            sx={{
                '& .MuiDialog-paper': {
                    position: 'relative',
                    height: '80vh',
                    width: '70vw',
                    maxWidth: 1000,
                    maxHeight: 800,
                    m: 'auto',
                    overflow: 'hidden', // prevent x scroll
                    display: 'flex',
                    flexDirection: 'column',
                },
                zIndex: (theme) => theme.zIndex.drawer + 999,
                backgroundColor: "rgba(0,0,0,0.3)"
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" component="span">Document Preview</Typography>
                <IconButton
                    edge="end"
                    color="inherit"
                    sx={{ position: 'relative', width: '2vw', height: '4vh' }}
                    onClick={onClose}
                    aria-label="close"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent
                sx={{
                    ...(mimeType === "application/pdf"
                        ? {
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '70vh',
                            padding: 2,
                            width: '100%',
                            height: '100%',
                            overflowY: 'auto', // only vertical scroll
                            '&::-webkit-scrollbar': { width: '4px' },
                            '&::-webkit-scrollbar-track': { background: 'transparent' },
                            '&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: '4px' },
                        }
                        : (['image/png', 'image/jpg', 'image/jpeg'].includes(mimeType)
                            ? {
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                background: '#f5f5f5',
                                minHeight: '60vh',
                                width: '100%',
                                height: '100%',
                                padding: 2,
                            }
                            : {
                                overflow: 'auto',
                                textAlign: 'center',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                                height: '100%',
                            })
                    )
                }}>
                {mimeType === "application/pdf" ? (
                    <Box sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        maxHeight: '65vh',
                        overflowY: 'auto',
                    }}>
                        <Document
                            file={url}
                            onLoadSuccess={onDocumentLoadSuccess}
                            loading={
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
                                    <CircularProgress />
                                    <Typography sx={{ mt: 2 }}>Loading PDF...</Typography>
                                </Box>
                            }
                            error={<Typography sx={{ mt: 4, color: 'error.main' }}>Failed to load PDF.</Typography>}
                        >
                            <Page
                                pageNumber={currentPage}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                                width={Math.min(window.innerWidth * 0.65, 900)} // match dialog width minus padding
                            />
                        </Document>
                    </Box>
                ) : (['image/png', 'image/jpg', 'image/jpeg'].includes(mimeType) ? (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: '100%',
                        background: '#f5f5f5',
                    }}>
                        <img
                            src={url}
                            alt="Document Preview"
                            style={{
                                maxWidth: '90%',
                                maxHeight: '60vh',
                                objectFit: 'contain',
                                borderRadius: 8,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                        />
                    </Box>
                ) : (
                    <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
                        <Typography variant="body1">Preview not available for this file type.</Typography>
                    </Box>
                ))}
            </DialogContent>

            {/* Fixed navigation for multi-page PDF (now absolutely positioned inside dialog) */}
            {mimeType === "application/pdf" && numPages > 1 && (
                <Box sx={{
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                    width: '100%',
                    zIndex: 10,
                    background: 'rgba(255,255,255,0.95)',
                    boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
                    py: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                    <Button
                        onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                        disabled={currentPage === 1}
                        variant="outlined"
                        sx={{ minWidth: 80 }}
                    >
                        Prev
                    </Button>
                    <Typography sx={{ mx: 2 }}>
                        Page {currentPage} of {numPages}
                    </Typography>
                    <Button
                        onClick={() => setCurrentPage(p => Math.min(p + 1, numPages))}
                        disabled={currentPage === numPages}
                        variant="outlined"
                        sx={{ minWidth: 80 }}
                    >
                        Next
                    </Button>
                </Box>
            )}
        </Dialog>
    );
}
export default DocumentPreview;